// src/components/common/CustomDataGridDesktop.tsx - MEJORADO PARA LAYOUT GLOBAL

import React from 'react';
import { Datagrid, FunctionField, DatagridProps, RaRecord } from 'react-admin';
import { Box, useTheme, useMediaQuery } from '@mui/material';

interface CustomDataGridDesktopProps<RecordType extends RaRecord = any> 
  extends DatagridProps<RecordType> {
  children: React.ReactNode;
  /**
   * Altura máxima del contenedor de tabla
   * @default 'calc(100vh - 350px)'
   */
  maxHeight?: string;
  /**
   * Si debe usar scroll sticky para headers
   * @default true
   */
  stickyHeaders?: boolean;
}

/**
 * CustomDataGridDesktop - MEJORADO para funcionar con el layout global
 * 
 * ✅ FUNCIONA CON:
 * - Nuevo CustomLayout
 * - ListWrapper
 * - Paginación siempre visible
 */
const CustomDataGridDesktop = <RecordType extends RaRecord = any>({
  children,
  sx,
  maxHeight = 'calc(100vh - 350px)',
  stickyHeaders = true,
  ...props
}: CustomDataGridDesktopProps<RecordType>) => {
  const theme = useTheme();

  // ✅ Estilos optimizados para el nuevo layout
  const combinedSx = {
    // ✅ Estilos básicos de tabla
    '& .MuiTableCell-root': {
      padding: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    
    // ✅ Headers mejorados
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `2px solid ${theme.palette.divider}`,
      
      // ✅ Headers sticky si está habilitado
      ...(stickyHeaders && {
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }),
    },
    
    // ✅ Filas mejoradas
    '& .MuiTableBody-root .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      
      '&:last-child .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
    
    // ✅ Deshabilitar click en filas por defecto
    '& .RaDatagrid-clickableRow': {
      cursor: 'default',
    },
    
    // ✅ Scroll mejorado
    '& .MuiTableContainer-root': {
      maxHeight,
      overflow: 'auto',
      
      // ✅ Scroll suave
      scrollBehavior: 'smooth',
      
      // ✅ Estilos de scrollbar en webkit
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[100],
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[400],
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: theme.palette.grey[600],
        },
      },
    },
    
    // ✅ Responsive en tablets
    [theme.breakpoints.down('md')]: {
      '& .MuiTableCell-root': {
        padding: theme.spacing(0.5),
        fontSize: '0.875rem',
      },
    },
    
  
    ...sx,
  };

  return (
    <Box 
      className="custom-datagrid-container"
      sx={{ 
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        overflow: 'hidden', 
      }}
    >
   
      <Box 
        sx={{ 
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Datagrid
          {...props}
          bulkActionButtons={false}
          size="small"
          rowClick={false}
          sx={combinedSx}
        >
          {React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) {
              console.warn(`[CustomDataGridDesktop] Invalid child at index ${index}:`, child);
              return null;
            }

            const childProps = child.props || {};
            const { source, label, sortable } = childProps;

        
            if (
              source === undefined &&
              label === "" &&
              sortable === false
            ) {
              return (
                <FunctionField
                  key={index}
                  label=""
                  sortable={false}
                  render={(record: RecordType) => {
                    if (!record) {
                      console.warn(`[CustomDataGridDesktop] FunctionField record is null or undefined.`);
                      return null;
                    }

                    // ✅ Clonar elemento con props del record
                    return React.cloneElement(
                      child as React.ReactElement<{ record: RecordType }>, 
                      { record }
                    );
                  }}
                  sx={{
                    // ✅ Columna de acciones más estrecha
                    '& .MuiTableCell-root': {
                      width: '120px',
                      textAlign: 'center',
                    },
                  }}
                />
              );
            }

            // ✅ Para otros campos, devolver tal como están
            return child;
          })}
        </Datagrid>
      </Box>
      
      {/* ✅ Espacio reservado para paginación - se maneja automáticamente por React Admin */}
    </Box>
  );
};

export default CustomDataGridDesktop;